import { createAsyncThunk, createSlice, isAnyOf } from "@reduxjs/toolkit";
import {
  DumpsterForm,
  GetDumpstersParams,
  SetDumpsterLocationParams,
  triplePSDatasource,
} from "../../datasource";
import {
  APIResult,
  emptyAPIResult,
  loadingAPIResult,
  successAPIResult,
  errorAPIResult,
} from "../../lib/redux";
import { Dumpster, DumpsterLocationHistory } from "../../models";
import { logout } from "../Auth/authSlice";

export interface DumpsterState {
  getDumpsters: APIResult<Dumpster[]>;
  getDumpster: APIResult<Dumpster>;
  createDumpster: APIResult<Dumpster>;
  updateDumpster: APIResult<Dumpster>;
  deleteDumpster: APIResult<string>;
  setDumpsterLocation: APIResult<Dumpster>;
  getDumpsterLocationHistory: APIResult<DumpsterLocationHistory[]>;

  dumpsters: Dumpster[];
  selectedDumpster?: Dumpster;
  locationHistory: DumpsterLocationHistory[];
  showDeleteDumpsterConfirmationModal?: Dumpster;
  showSetLocationModal?: Dumpster;
  dumpsterToRemove?: string;
  showAddDumpsterModal: boolean;
  showEditDumpsterModal: boolean;
}

const initialState: DumpsterState = {
  getDumpsters: emptyAPIResult(),
  getDumpster: emptyAPIResult(),
  createDumpster: emptyAPIResult(),
  updateDumpster: emptyAPIResult(),
  deleteDumpster: emptyAPIResult(),
  setDumpsterLocation: emptyAPIResult(),
  getDumpsterLocationHistory: emptyAPIResult(),

  dumpsters: [],
  locationHistory: [],
  showAddDumpsterModal: false,
  showEditDumpsterModal: false,
};

export const getDumpsters = createAsyncThunk(
  "dumpsters/getDumpsters",
  async (params: GetDumpstersParams) => {
    return triplePSDatasource.getDumpsters(params);
  }
);

export const getDumpster = createAsyncThunk(
  "dumpsters/getDumpster",
  async (id: string) => {
    return triplePSDatasource.getDumpster(id);
  }
);

export const createDumpster = createAsyncThunk(
  "dumpsters/createDumpster",
  async (form: DumpsterForm) => {
    return triplePSDatasource.createDumpster(form);
  }
);

export const updateDumpster = createAsyncThunk(
  "dumpsters/updateDumpster",
  async (dumpster: Dumpster) => {
    return triplePSDatasource.updateDumpster(dumpster);
  }
);

export const deleteDumpster = createAsyncThunk(
  "dumpsters/deleteDumpster",
  async (id: string) => {
    return triplePSDatasource.deleteDumpster(id);
  }
);

export const setDumpsterLocation = createAsyncThunk(
  "dumpsters/setDumpsterLocation",
  async (params: SetDumpsterLocationParams) => {
    return triplePSDatasource.setDumpsterLocation(params);
  }
);

export const getDumpsterLocationHistory = createAsyncThunk(
  "dumpsters/getDumpsterLocationHistory",
  async (dumpsterId: string) => {
    return triplePSDatasource.getDumpsterLocationHistory(dumpsterId);
  }
);

export const dumpstersSlice = createSlice({
  name: "dumpsters",
  initialState,
  reducers: {
    setDumpsterToRemove(state, action) {
      state.dumpsterToRemove = action.payload;
    },
    setSelectedDumpster(state, action) {
      state.selectedDumpster = action.payload;
    },
    toggleShowAddDumpsterModal(state) {
      state.showAddDumpsterModal = !state.showAddDumpsterModal;
    },
    toggleShowEditDumpsterModal(state) {
      state.showEditDumpsterModal = !state.showEditDumpsterModal;
    },
    showDeleteDumpsterConfirmationModal(state, action) {
      state.showDeleteDumpsterConfirmationModal = action.payload;
    },
    resetShowDeleteDumpsterConfirmationModal(state) {
      state.showDeleteDumpsterConfirmationModal = undefined;
    },
    setShowSetLocationModal(state, action) {
      state.showSetLocationModal = action.payload;
    },
    resetShowSetLocationModal(state) {
      state.showSetLocationModal = undefined;
    },
    resetDumpsterDetails(state) {
      state.getDumpster = emptyAPIResult();
      state.getDumpsterLocationHistory = emptyAPIResult();
      state.selectedDumpster = undefined;
      state.locationHistory = [];
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(getDumpsters.pending, (state) => {
        state.getDumpsters = loadingAPIResult();
      })
      .addCase(getDumpsters.fulfilled, (state, action) => {
        state.getDumpsters = successAPIResult(action.payload);
        state.dumpsters = action.payload;
      })
      .addCase(getDumpsters.rejected, (state, action) => {
        state.getDumpsters = errorAPIResult(action.error);
      })
      .addCase(getDumpster.pending, (state) => {
        state.getDumpster = loadingAPIResult();
      })
      .addCase(getDumpster.fulfilled, (state, action) => {
        state.getDumpster = successAPIResult(action.payload);
        state.selectedDumpster = action.payload;
      })
      .addCase(getDumpster.rejected, (state, action) => {
        state.getDumpster = errorAPIResult(action.error);
      })
      .addCase(createDumpster.pending, (state) => {
        state.createDumpster = loadingAPIResult();
      })
      .addCase(createDumpster.fulfilled, (state, action) => {
        state.createDumpster = successAPIResult(action.payload);
        state.dumpsters.push(action.payload);
        state.showAddDumpsterModal = false;
      })
      .addCase(createDumpster.rejected, (state, action) => {
        state.createDumpster = errorAPIResult(action.error);
      })
      .addCase(updateDumpster.pending, (state) => {
        state.updateDumpster = loadingAPIResult();
      })
      .addCase(updateDumpster.fulfilled, (state, action) => {
        state.updateDumpster = successAPIResult(action.payload);
        const index = state.dumpsters.findIndex(
          (d) => d.id === action.payload.id
        );
        if (index !== -1) {
          state.dumpsters[index] = action.payload;
        }
        state.selectedDumpster = action.payload;
        state.showEditDumpsterModal = false;
      })
      .addCase(updateDumpster.rejected, (state, action) => {
        state.updateDumpster = errorAPIResult(action.error);
      })
      .addCase(deleteDumpster.pending, (state) => {
        state.deleteDumpster = loadingAPIResult();
      })
      .addCase(deleteDumpster.fulfilled, (state, action) => {
        state.deleteDumpster = successAPIResult(action.payload);
        state.dumpsters = state.dumpsters
          ? state.dumpsters.filter((d) => d.id !== action.payload)
          : [];
        state.dumpsterToRemove = undefined;
        state.showDeleteDumpsterConfirmationModal = undefined;
      })
      .addCase(deleteDumpster.rejected, (state, action) => {
        state.deleteDumpster = errorAPIResult(action.error);
      })
      .addCase(setDumpsterLocation.pending, (state) => {
        state.setDumpsterLocation = loadingAPIResult();
      })
      .addCase(setDumpsterLocation.fulfilled, (state, action) => {
        state.setDumpsterLocation = successAPIResult(action.payload);
        const index = state.dumpsters.findIndex(
          (d) => d.id === action.payload.id
        );
        if (index !== -1) {
          state.dumpsters[index] = action.payload;
        }
        state.showSetLocationModal = undefined;
      })
      .addCase(setDumpsterLocation.rejected, (state, action) => {
        state.setDumpsterLocation = errorAPIResult(action.error);
      })
      .addCase(getDumpsterLocationHistory.pending, (state) => {
        state.getDumpsterLocationHistory = loadingAPIResult();
      })
      .addCase(getDumpsterLocationHistory.fulfilled, (state, action) => {
        state.getDumpsterLocationHistory = successAPIResult(action.payload);
        state.locationHistory = action.payload;
      })
      .addCase(getDumpsterLocationHistory.rejected, (state, action) => {
        state.getDumpsterLocationHistory = errorAPIResult(action.error);
      })
      .addMatcher(isAnyOf(logout), (state) => {
        state.getDumpsters = initialState.getDumpsters;
        state.getDumpster = initialState.getDumpster;
        state.createDumpster = initialState.createDumpster;
        state.updateDumpster = initialState.updateDumpster;
        state.deleteDumpster = initialState.deleteDumpster;
        state.setDumpsterLocation = initialState.setDumpsterLocation;
        state.getDumpsterLocationHistory = initialState.getDumpsterLocationHistory;

        state.dumpsters = initialState.dumpsters;
        state.locationHistory = initialState.locationHistory;
        state.selectedDumpster = undefined;
        state.dumpsterToRemove = undefined;
        state.showAddDumpsterModal = initialState.showAddDumpsterModal;
        state.showEditDumpsterModal = initialState.showEditDumpsterModal;
        state.showDeleteDumpsterConfirmationModal = undefined;
      });
  },
});

export const {
  setDumpsterToRemove,
  setSelectedDumpster,
  toggleShowAddDumpsterModal,
  toggleShowEditDumpsterModal,
  showDeleteDumpsterConfirmationModal,
  resetShowDeleteDumpsterConfirmationModal,
  setShowSetLocationModal,
  resetShowSetLocationModal,
  resetDumpsterDetails,
} = dumpstersSlice.actions;

export default dumpstersSlice.reducer;
